import { FC, useState, useMemo } from 'react';
import {
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import { useGuardianState } from '../../contexts/guardian.context';
import { useTranslation } from 'src/hooks/useTranslationHook';
import {
  useGuardianTransportHook,
  useGuardianTransportHistoryHook,
} from '../../hooks/useGuardianTransportHook';
import {
  Person,
  DirectionsBus,
  Route,
  AccessTime,
  AttachMoney,
  History,
  Phone,
  Email,
  LocationOn,
} from '@mui/icons-material';
import PaginatedGrid from '../../../../components/paginatedGrid/PaginatedGrid';
import { GridColDef } from '@mui/x-data-grid';
import { GuardianTransportHistory } from 'src/apis/guardian/sub-apis/useGuardianTransportService';

const GuardianTransportView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedChildId } = useGuardianState();
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(10);

  const {
    data: transportData,
    isLoading,
    error,
  } = useGuardianTransportHook(selectedChildId || '');

  const {
    data: historyData,
    isLoading: isLoadingHistory,
  } = useGuardianTransportHistoryHook(
    selectedChildId || '',
    {
      page: historyPage,
      limit: historyPageSize,
    }
  );

  const transportHistory = historyData?.data || [];
  const historyTotalCount = historyData?.count ?? 0;

  const historyColumns: GridColDef<GuardianTransportHistory>[] = useMemo(
    () => [
      {
        field: 'date',
        headerName: translateText('guardian.transport.history.columns.date'),
        width: 150,
        renderCell: (params) => {
          const date = new Date(params.value);
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          });
        },
      },
      {
        field: 'routeName',
        headerName: translateText('guardian.transport.history.columns.routeName'),
        width: 200,
      },
      {
        field: 'driverName',
        headerName: translateText('guardian.transport.history.columns.driverName'),
        width: 180,
      },
      {
        field: 'vehicleNumber',
        headerName: translateText('guardian.transport.history.columns.vehicleNumber'),
        width: 150,
      },
      {
        field: 'kilometers',
        headerName: translateText('guardian.transport.history.columns.kilometers'),
        width: 120,
        renderCell: (params) => `${params.value} km`,
      },
      {
        field: 'pickupTime',
        headerName: translateText('guardian.transport.history.columns.pickupTime'),
        width: 120,
      },
      {
        field: 'dropTime',
        headerName: translateText('guardian.transport.history.columns.dropTime'),
        width: 120,
      },
      {
        field: 'status',
        headerName: translateText('guardian.transport.history.columns.status'),
        width: 130,
        renderCell: (params) => {
          const statusColors = {
            completed: 'success',
            missed: 'error',
            cancelled: 'warning',
          } as const;
          return (
            <Chip
              label={translateText(
                `guardian.transport.history.status.${params.value}`
              )}
              color={statusColors[params.value as keyof typeof statusColors] || 'default'}
              size="small"
            />
          );
        },
      },
    ],
    [translateText]
  );

  if (!selectedChildId) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-transport-container">
        <Grid item xs={12}>
          <Alert severity="info">
            {translateText('guardian.selectChild')}
          </Alert>
        </Grid>
      </Grid>
    );
  }

  if (isLoading) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-transport-container">
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              {translateText('common.loading')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (error || !transportData) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-transport-container">
        <Grid item xs={12}>
          <Alert severity="error">
            {translateText('common.error')}
          </Alert>
        </Grid>
      </Grid>
    );
  }

  const { route, driver, vehicle, studentStop, transportFee } = transportData;

  return (
    <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-transport-container">
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom>
          {translateText('guardian.transport.title')}
        </Typography>
      </Grid>

      {/* Driver Details Card */}
      {driver && (
        <Grid item xs={12} md={6}>
          <Card data-testid="driver-details-card">
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Person sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">
                  {translateText('guardian.transport.driverDetails.title')}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {translateText('guardian.transport.driverDetails.name')}
                  </Typography>
                  <Typography variant="body1">{driver.name}</Typography>
                </Grid>
                {driver.phoneNumber && (
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center">
                      <Phone sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                      <Typography variant="body2">{driver.phoneNumber}</Typography>
                    </Box>
                  </Grid>
                )}
                {driver.email && (
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center">
                      <Email sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                      <Typography variant="body2">{driver.email}</Typography>
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {translateText('guardian.transport.driverDetails.licenseNumber')}
                  </Typography>
                  <Typography variant="body2">{driver.licenseNumber}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {translateText('guardian.transport.driverDetails.licenseType')}
                  </Typography>
                  <Typography variant="body2">
                    {driver.licenseType.replace('_', ' ')}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Route Overview Card */}
      {route && (
        <Grid item xs={12} md={6}>
          <Card data-testid="route-overview-card">
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Route sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">
                  {translateText('guardian.transport.routeOverview.title')}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {translateText('guardian.transport.routeOverview.routeName')}
                  </Typography>
                  <Typography variant="body1">{route.routeName}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {translateText('guardian.transport.routeOverview.startLocation')}
                  </Typography>
                  <Typography variant="body2">{route.startLocation}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {translateText('guardian.transport.routeOverview.endLocation')}
                  </Typography>
                  <Typography variant="body2">{route.endLocation}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {translateText('guardian.transport.routeOverview.distance')}
                  </Typography>
                  <Typography variant="body2">{route.distance} km</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {translateText('guardian.transport.routeOverview.estimatedDuration')}
                  </Typography>
                  <Typography variant="body2">{route.estimatedDuration} min</Typography>
                </Grid>
                {vehicle && (
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center">
                      <DirectionsBus sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        {vehicle.vehicleNumber} ({vehicle.vehicleType})
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Pickup and Drop Times Card */}
      {studentStop && (
        <Grid item xs={12} md={6}>
          <Card data-testid="pickup-drop-times-card">
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <AccessTime sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">
                  {translateText('guardian.transport.pickupDropTimes.title')}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <LocationOn sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      {translateText('guardian.transport.pickupDropTimes.stopName')}
                    </Typography>
                  </Box>
                  <Typography variant="body1">{studentStop.stopName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {studentStop.location}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {translateText('guardian.transport.pickupDropTimes.pickupTime')}
                  </Typography>
                  <Typography variant="h6" color="primary.main">
                    {studentStop.pickupTime}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {translateText('guardian.transport.pickupDropTimes.dropTime')}
                  </Typography>
                  <Typography variant="h6" color="primary.main">
                    {studentStop.dropTime}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Transport Fees Card */}
      {transportFee && (
        <Grid item xs={12} md={6}>
          <Card data-testid="transport-fees-card">
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <AttachMoney sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">
                  {translateText('guardian.transport.transportFees.title')}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {translateText('guardian.transport.transportFees.ratePerKm')}
                  </Typography>
                  <Typography variant="body1">
                    ₹{transportFee.ratePerKm} / km
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {translateText('guardian.transport.transportFees.distanceFromHome')}
                  </Typography>
                  <Typography variant="body1">
                    {transportFee.distanceFromHome} km
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" fontWeight="bold">
                      {translateText('guardian.transport.transportFees.totalFee')}
                    </Typography>
                    <Typography variant="h6" color="primary.main" fontWeight="bold">
                      ₹{transportFee.totalFee}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {transportFee.distanceFromHome} km × ₹{transportFee.ratePerKm} / km
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Transport History */}
      <Grid item xs={12}>
        <Card data-testid="transport-history-card">
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <History sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">
                {translateText('guardian.transport.history.title')}
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {isLoadingHistory ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : transportHistory.length === 0 ? (
              <Alert severity="info">
                {translateText('guardian.transport.history.noHistory')}
              </Alert>
            ) : (
              <PaginatedGrid
                rows={transportHistory}
                columns={historyColumns}
                currentPage={historyPage}
                currentPageSize={historyPageSize}
                totalCount={historyTotalCount}
                onPageChange={(newPage: number, newPageSize: number) => {
                  setHistoryPage(newPage);
                  setHistoryPageSize(newPageSize);
                }}
                data-testid="transport-history-grid"
              />
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default GuardianTransportView;
